/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  CanonicalToolAttributes,
  CanonicalToolAttributes$inboundSchema,
} from "./canonicaltoolattributes.js";
import {
  ToolAnnotations,
  ToolAnnotations$inboundSchema,
} from "./toolannotations.js";
import { ToolVariation, ToolVariation$inboundSchema } from "./toolvariation.js";

/**
 * The template engine
 */
export const Engine = {
  Mustache: "mustache",
} as const;
/**
 * The template engine
 */
export type Engine = OpenEnum<typeof Engine>;

/**
 * The kind of prompt the template is used for
 */
export const PromptTemplateKind = {
  Prompt: "prompt",
  HigherOrderTool: "higher_order_tool",
} as const;
/**
 * The kind of prompt the template is used for
 */
export type PromptTemplateKind = OpenEnum<typeof PromptTemplateKind>;

/**
 * A prompt template
 */
export type PromptTemplate = {
  /**
   * Tool annotations providing behavioral hints about the tool
   */
  annotations?: ToolAnnotations | undefined;
  /**
   * The original details of a tool
   */
  canonical?: CanonicalToolAttributes | undefined;
  /**
   * The canonical name of the tool. Will be the same as the name if there is no variation.
   */
  canonicalName: string;
  /**
   * Confirmation mode for the tool
   */
  confirm?: string | undefined;
  /**
   * Prompt for the confirmation
   */
  confirmPrompt?: string | undefined;
  /**
   * The creation date of the tool.
   */
  createdAt: Date;
  /**
   * Description of the tool
   */
  description: string;
  /**
   * The template engine
   */
  engine: Engine;
  /**
   * The revision tree ID for the prompt template
   */
  historyId: string;
  /**
   * The ID of the tool
   */
  id: string;
  /**
   * The kind of prompt the template is used for
   */
  kind: PromptTemplateKind;
  /**
   * The name of the tool
   */
  name: string;
  /**
   * The previous version of the prompt template to use as predecessor
   */
  predecessorId?: string | undefined;
  /**
   * The ID of the project
   */
  projectId: string;
  /**
   * The template content
   */
  prompt: string;
  /**
   * JSON schema for the request
   */
  schema: string;
  /**
   * Version of the schema
   */
  schemaVersion?: string | undefined;
  /**
   * Summarizer for the tool
   */
  summarizer?: string | undefined;
  /**
   * The URN of this tool
   */
  toolUrn: string;
  /**
   * The suggested tool URNS associated with the prompt template
   */
  toolUrnsHint?: Array<string> | undefined;
  /**
   * The suggested tool names associated with the prompt template
   */
  toolsHint: Array<string>;
  /**
   * The last update date of the tool.
   */
  updatedAt: Date;
  variation?: ToolVariation | undefined;
};

/** @internal */
export const Engine$inboundSchema: z.ZodMiniType<Engine, unknown> = openEnums
  .inboundSchema(Engine);

/** @internal */
export const PromptTemplateKind$inboundSchema: z.ZodMiniType<
  PromptTemplateKind,
  unknown
> = openEnums.inboundSchema(PromptTemplateKind);

/** @internal */
export const PromptTemplate$inboundSchema: z.ZodMiniType<
  PromptTemplate,
  unknown
> = z.pipe(
  z.object({
    annotations: z.optional(ToolAnnotations$inboundSchema),
    canonical: z.optional(CanonicalToolAttributes$inboundSchema),
    canonical_name: z.string(),
    confirm: z.optional(z.string()),
    confirm_prompt: z.optional(z.string()),
    created_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    description: z.string(),
    engine: Engine$inboundSchema,
    history_id: z.string(),
    id: z.string(),
    kind: PromptTemplateKind$inboundSchema,
    name: z.string(),
    predecessor_id: z.optional(z.string()),
    project_id: z.string(),
    prompt: z.string(),
    schema: z.string(),
    schema_version: z.optional(z.string()),
    summarizer: z.optional(z.string()),
    tool_urn: z.string(),
    tool_urns_hint: z.optional(z.array(z.string())),
    tools_hint: z.array(z.string()),
    updated_at: z.pipe(
      z.iso.datetime({ offset: true }),
      z.transform(v => new Date(v)),
    ),
    variation: z.optional(ToolVariation$inboundSchema),
  }),
  z.transform((v) => {
    return remap$(v, {
      "canonical_name": "canonicalName",
      "confirm_prompt": "confirmPrompt",
      "created_at": "createdAt",
      "history_id": "historyId",
      "predecessor_id": "predecessorId",
      "project_id": "projectId",
      "schema_version": "schemaVersion",
      "tool_urn": "toolUrn",
      "tool_urns_hint": "toolUrnsHint",
      "tools_hint": "toolsHint",
      "updated_at": "updatedAt",
    });
  }),
);

export function promptTemplateFromJSON(
  jsonString: string,
): SafeParseResult<PromptTemplate, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => PromptTemplate$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'PromptTemplate' from JSON`,
  );
}
